import { useState } from 'react'
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router'
import { type ColumnDef, type OnChangeFn, type SortingState } from '@tanstack/react-table'
import { Bookmark, Eye, EyeOff, Plus, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable, SortableHeader } from '@/components/ui/data-table'
import { EmptyState } from '@/components/ui/empty-state'
import { PageHeaderActions } from '@/components/layout/page-header'
import { EmailLink } from '@/components/ui/email-link'
import { ContactPhoneLink } from '@/components/ui/phone-link'
import { ContactFilters } from '@/components/contacts/contact-filters'
import { ContactForm } from '@/components/contacts/contact-form'
import { SaveViewDialog } from '@/components/shared/save-view-dialog'
import { ViewTabs } from '@/components/shared/view-tabs'
import { useAccounts } from '@/hooks/use-accounts'
import { useAccountDivisions } from '@/hooks/use-account-divisions'
import { useContactsList } from '@/hooks/use-contacts'
import { relativeTime } from '@/lib/utils'
import { DEFAULT_PAGE_SIZE, pageCount } from '@/lib/list-query'

export const Route = createFileRoute('/_authed/contacts/')({
  component: ContactsIndex,
})

type ContactRow = NonNullable<ReturnType<typeof useContactsList>['data']>['rows'][number]

const CONTACT_TYPE_LABELS: Record<string, string> = {
  reseller_rep: 'Reseller Rep',
  end_user_contact: 'End User Contact',
  distributor_rep: 'Distributor Rep',
}

// Table column id ↔ saved/URL sort_by key ('name' sorts last_name+first_name
// server-side; 'account' sorts by the embedded account's name).
const COLUMN_TO_SORT_BY: Record<string, string> = {
  name: 'last_name',
  account: 'account_name',
  updated_at: 'updated_at',
}
const SORT_BY_TO_COLUMN: Record<string, string> = {
  last_name: 'name',
  account_name: 'account',
  updated_at: 'updated_at',
}

function ContactsIndex() {
  const navigate = useNavigate({ from: Route.fullPath })
  const search = useSearch({ from: '/_authed/contacts' })
  const [formOpen, setFormOpen] = useState(false)
  const [saveOpen, setSaveOpen] = useState(false)
  const showInactive = !!search.show_inactive
  const [pageIndex, setPageIndex] = useState(0)

  // Any filter/sort change invalidates the current page position.
  const filterKey = JSON.stringify([
    search.account_id, search.division_id, search.contact_type, search.has_email,
    search.search, showInactive, search.sort_by, search.sort_dir,
  ])
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey)
    setPageIndex(0)
  }

  // Filters, sort, and paging all apply server-side.
  const { data, isLoading } = useContactsList({
    pageIndex,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: search.sort_by,
    sortDir: search.sort_dir,
    showInactive,
    accountId: search.account_id,
    divisionId: search.division_id,
    contactType: search.contact_type,
    hasEmail: search.has_email,
    search: search.search,
  })
  const contacts = data?.rows ?? []
  const total = data?.total ?? 0

  const sorting: SortingState = [
    {
      id: SORT_BY_TO_COLUMN[search.sort_by] ?? 'name',
      desc: search.sort_dir === 'desc',
    },
  ]
  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater
    void navigate({
      to: '/contacts',
      search: (prev) => ({
        ...prev,
        sort_by: COLUMN_TO_SORT_BY[next[0]?.id ?? 'name'] ?? 'last_name',
        sort_dir: next[0]?.desc ? ('desc' as const) : ('asc' as const),
      }),
    })
  }

  const { data: accounts = [] } = useAccounts()
  const { data: scopeDivisions = [] } = useAccountDivisions(
    search.account_id ?? ''
  )

  const columns: ColumnDef<ContactRow>[] = [
    {
      id: 'name',
      accessorFn: (row) => `${row.last_name} ${row.first_name}`,
      header: ({ column }) => <SortableHeader label="Name" column={column} />,
      cell: ({ row }) => (
        <span
          className={`font-medium ${!row.original.is_active ? 'text-muted-foreground' : ''}`}
        >
          {row.original.first_name} {row.original.last_name}
        </span>
      ),
    },
    {
      accessorKey: 'title',
      header: 'Title',
      cell: ({ row }) => (
        <span className="text-sm">{row.original.title ?? '—'}</span>
      ),
    },
    {
      id: 'account',
      header: ({ column }) => (
        <SortableHeader label="Account" column={column} />
      ),
      accessorFn: (row) =>
        (row as ContactRow & { account?: { name: string } | null }).account
          ?.name ?? '',
      cell: ({ row }) => {
        const acct = (
          row.original as ContactRow & {
            account?: { id: string; name: string } | null
          }
        ).account
        return acct ? (
          <Link
            to="/accounts/$accountId"
            params={{ accountId: acct.id }}
            className="text-sm text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {acct.name}
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      id: 'division',
      header: 'Division',
      cell: ({ row }) => {
        const div = (
          row.original as ContactRow & { division?: { name: string } | null }
        ).division
        return <span className="text-sm">{div?.name ?? '—'}</span>
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => <EmailLink email={row.original.email} iconOnly />,
    },
    {
      id: 'phone',
      header: 'Phone',
      cell: ({ row }) => (
        <ContactPhoneLink contactId={row.original.id} compact />
      ),
    },
    {
      accessorKey: 'contact_type',
      header: 'Type',
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <Badge variant="secondary" className="text-xs font-normal">
            {CONTACT_TYPE_LABELS[row.original.contact_type] ??
              row.original.contact_type}
          </Badge>
          {!row.original.is_active && (
            <Badge
              variant="outline"
              className="text-xs font-normal text-muted-foreground"
            >
              Inactive
            </Badge>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'updated_at',
      header: ({ column }) => (
        <SortableHeader label="Updated" column={column} />
      ),
      cell: ({ row }) => (
        <span className="text-xs text-muted-foreground">
          {relativeTime(row.original.updated_at)}
        </span>
      ),
    },
  ]

  const hasActiveFilters = !!(
    search.search ||
    search.account_id ||
    search.division_id ||
    search.contact_type ||
    search.has_email
  )

  // Filter values only — view/sort/division scope are not part of a saved view's filters
  const filterOnly = {
    search: search.search,
    account_id: search.account_id,
    contact_type: search.contact_type,
    has_email: search.has_email,
    show_inactive: search.show_inactive,
  }

  const handleApplyView = (
    viewId: string | undefined,
    filters: Record<string, unknown>
  ) => {
    void navigate({
      to: '/contacts',
      search: () => ({
        sort_by: 'last_name',
        sort_dir: 'asc' as const,
        ...(filters as Partial<typeof search>),
        view: viewId,
      }),
    })
  }

  return (
    <div className="flex h-full flex-col">
      {/* Slice 1B-1: actions live in the ONE shared header via the portal
          slot — the title comes from the nav registry there. */}
      <PageHeaderActions>
        <Badge variant="secondary">{isLoading ? '…' : total}</Badge>
        {/* Same below-lg icon collapse as accounts (audit 1B-1 RETURN). */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Button
            variant={showInactive ? 'secondary' : 'outline'}
            size="sm"
            aria-pressed={showInactive}
            aria-label={showInactive ? 'Hide inactive' : 'Show inactive'}
            onClick={() =>
              void navigate({
                to: '/contacts',
                search: (prev) => ({
                  ...prev,
                  show_inactive: showInactive ? undefined : true,
                }),
              })
            }
          >
            {showInactive ? (
              <EyeOff className="h-4 w-4 lg:mr-1" />
            ) : (
              <Eye className="h-4 w-4 lg:mr-1" />
            )}
            <span className="hidden lg:inline">
              {showInactive ? 'Hide inactive' : 'Show inactive'}
            </span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            aria-label="Save view"
            onClick={() => setSaveOpen(true)}
          >
            <Bookmark className="h-4 w-4 lg:mr-1" />
            <span className="hidden lg:inline">Save view</span>
          </Button>
          <Button size="sm" aria-label="New contact" onClick={() => setFormOpen(true)}>
            <Plus className="h-4 w-4 lg:mr-1" />
            <span className="hidden lg:inline">New contact</span>
          </Button>
        </div>
      </PageHeaderActions>

      <ViewTabs
        scope="contacts"
        activeViewId={search.view}
        onSelect={handleApplyView}
      />

      {search.division_id && (
        <div className="flex items-center gap-2 border-b bg-muted/40 px-6 py-2">
          <span className="text-sm text-muted-foreground">
            Showing contacts for:{' '}
            <span className="font-medium text-foreground">
              {accounts.find((a) => a.id === search.account_id)?.name ?? '…'}
              {scopeDivisions.find((d) => d.id === search.division_id)
                ? ` — ${scopeDivisions.find((d) => d.id === search.division_id)!.name}`
                : ''}
            </span>
          </span>
          <button
            className="ml-auto rounded-sm opacity-70 hover:opacity-100"
            onClick={() =>
              void navigate({
                to: '/contacts',
                search: (prev) => ({
                  ...prev,
                  account_id: undefined,
                  division_id: undefined,
                }),
              })
            }
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="border-b px-6 py-3">
        <ContactFilters
          values={search}
          onChange={(next) =>
            void navigate({
              to: '/contacts',
              search: (prev) => ({ ...prev, ...(next as typeof prev) }),
            })
          }
          onClear={() =>
            void navigate({
              to: '/contacts',
              search: () => ({
                sort_by: 'last_name',
                sort_dir: 'asc' as const,
              }),
            })
          }
        />
      </div>

      <div className="flex-1 overflow-auto">
        <div className="px-6 py-4">
          <DataTable
            columns={columns}
            data={contacts}
            loading={isLoading}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            pagination={{
              pageIndex,
              pageSize: DEFAULT_PAGE_SIZE,
              pageCount: pageCount(total, DEFAULT_PAGE_SIZE),
              totalCount: total,
              onPageChange: setPageIndex,
            }}
            onRowClick={(row) =>
              void navigate({
                to: '/contacts/$contactId',
                params: { contactId: row.id },
              })
            }
            emptyState={
              hasActiveFilters ? (
                <EmptyState
                  title="No contacts match these filters"
                  description="Try clearing or adjusting your filters to see more."
                />
              ) : (
                <EmptyState
                  title="No contacts yet"
                  description="Contacts are the people at your accounts. Add one to track who you work with on each deal."
                  action={
                    <Button size="sm" data-testid="empty-new-contact" onClick={() => setFormOpen(true)}>
                      <Plus className="mr-1 h-4 w-4" />
                      New contact
                    </Button>
                  }
                />
              )
            }
          />
        </div>
      </div>

      <ContactForm open={formOpen} onOpenChange={setFormOpen} />
      <SaveViewDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        scope="contacts"
        filters={filterOnly}
        sortBy={search.sort_by}
        sortDir={search.sort_dir}
      />
    </div>
  )
}
